/**
 * Usage View — Credit usage analytics and breakdown.
 * Shows credit spending over time, by category, and recent transactions.
 */

import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getServices, type CreditBalance, type UsageEntry } from "../services/index.ts";

function getRemaining(b: CreditBalance): number {
  return b.monthlyCredits - b.monthlyUsed + b.bonusCredits;
}

const CATEGORY_LABELS: Record<string, { label: string; color: string; colorEnd: string; description: string }> = {
  chat: { label: "Chat", color: "#3B82F6", colorEnd: "#60A5FA", description: "Direct conversations with the assistant" },
  automation: { label: "Automations", color: "#8B5CF6", colorEnd: "#A78BFA", description: "Scheduled automated tasks" },
  channel: { label: "Channels", color: "#10B981", colorEnd: "#34D399", description: "Messages from Telegram, WhatsApp, etc." },
  tool: { label: "Tools", color: "#EC4899", colorEnd: "#F472B6", description: "Tool usage during conversations" },
  web: { label: "Web Search", color: "#F59E0B", colorEnd: "#FBBF24", description: "Web searches performed by the assistant" },
  image: { label: "Images", color: "#F97316", colorEnd: "#FB923C", description: "Image generation and analysis" },
  voice: { label: "Voice", color: "#14B8A6", colorEnd: "#2DD4BF", description: "Voice interactions" },
  purchase: { label: "Credit Purchase", color: "#6366F1", colorEnd: "#818CF8", description: "Credits added from purchases" },
};

@customElement("katanoid-usage-view")
export class KatanoidUsageView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ attribute: false }) credits: CreditBalance | null = null;
  @state() private usage: UsageEntry[] = [];
  @state() private loading = true;

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadUsage();
  }

  private async loadUsage(): Promise<void> {
    this.loading = true;
    try {
      const { credits } = getServices();
      this.usage = await credits.getUsageBreakdown();
    } catch {
      this.usage = [];
    }
    this.loading = false;
  }

  protected override render(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Usage</h1>
          <button class="btn btn--ghost btn--sm" @click=${() => this.loadUsage()}>
            Refresh
          </button>
        </div>

        ${this.renderCreditOverview()}
        ${this.loading
          ? html`<div class="usage-loading"><div class="spinner"></div></div>`
          : html`
              ${this.renderUsageBreakdown()}
              ${this.renderResetInfo()}
            `}
      </div>
    `;
  }

  private renderCreditOverview(): TemplateResult {
    if (!this.credits) {
      return html`
        <div class="usage-overview">
          <div class="shimmer" style="height: 120px; border-radius: var(--radius-lg);"></div>
        </div>
      `;
    }

    const remaining = getRemaining(this.credits);
    const used = this.credits.monthlyUsed;
    const total = this.credits.monthlyCredits + this.credits.bonusCredits;
    const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
    const level = pct > 40 ? "high" : pct > 20 ? "medium" : "low";

    return html`
      <div class="usage-overview">
        <div class="usage-overview-bar">
          <div class="usage-overview-numbers">
            <div class="usage-overview-remaining">
              <span class="usage-overview-big">${remaining}</span>
              <span class="usage-overview-label">remaining</span>
            </div>
            <div class="usage-overview-used">
              <span class="usage-overview-big">${used}</span>
              <span class="usage-overview-label">used this month</span>
            </div>
          </div>
          <div class="credit-bar" style="margin-top: var(--space-4);">
            <div class="credit-bar-track">
              <div
                class="credit-bar-fill ${level === "low" ? "credit-bar-fill--critical" : level === "medium" ? "credit-bar-fill--low" : ""}"
                style="width: ${pct}%"
              ></div>
            </div>
          </div>
          <div class="usage-overview-detail">
            <span>${this.credits.monthlyCredits - used} monthly + ${this.credits.bonusCredits} bonus remaining</span>
            <button class="usage-buy-link" @click=${this.handleBuyCredits}>Buy more credits</button>
          </div>
        </div>
      </div>
    `;
  }

  private handleBuyCredits(): void {
    this.dispatchEvent(
      new CustomEvent("billing-action", {
        detail: { action: "buy-credits" },
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderUsageBreakdown(): TemplateResult {
    // Filter out purchase transactions — those are credit additions, not consumption
    const consumed = this.usage.filter((u) => u.category !== "purchase");

    if (consumed.length === 0) {
      return html`
        <div class="usage-empty">
          <p class="usage-empty-text">No usage data yet this month. Start chatting to see your breakdown.</p>
        </div>
      `;
    }

    const totalUsed = consumed.reduce((sum, u) => sum + u.credits, 0);

    return html`
      <div class="settings-section">
        <div class="settings-section-title">Breakdown</div>
        <div class="usage-breakdown-card">
          <div class="usage-bar">
            ${consumed.map((u) => {
              const cat = CATEGORY_LABELS[u.category] ?? { label: u.category, color: "var(--text-tertiary)", colorEnd: "var(--text-tertiary)", description: "" };
              const width = totalUsed > 0 ? (u.credits / totalUsed) * 100 : 0;
              return html`
                <div
                  class="usage-bar-segment"
                  style="width: ${width}%; background: linear-gradient(135deg, ${cat.color} 0%, ${cat.colorEnd} 100%);"
                  title="${cat.label}: ${u.credits} credits"
                ></div>
              `;
            })}
          </div>
          <div class="usage-category-list">
            ${consumed.map((u) => {
              const cat = CATEGORY_LABELS[u.category] ?? { label: u.category, color: "var(--text-tertiary)", description: "" };
              const pct = totalUsed > 0 ? Math.round((u.credits / totalUsed) * 100) : 0;
              return html`
                <div class="usage-category-row">
                  <div class="usage-category">
                    <span class="usage-category-dot" style="background: ${cat.color};"></span>
                    <div class="usage-category-info">
                      <span class="usage-category-label">${cat.label}</span>
                      <span class="usage-category-desc">${cat.description}</span>
                    </div>
                  </div>
                  <div class="usage-category-stats">
                    <span class="usage-category-value">${u.credits}</span>
                    <span class="usage-category-pct">${pct}%</span>
                  </div>
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }

  private renderResetInfo(): TemplateResult {
    if (!this.credits?.resetDate) return html``;

    const resetDate = new Date(this.credits.resetDate);
    const now = new Date();
    const daysLeft = Math.max(0, Math.ceil((resetDate.getTime() - now.getTime()) / 86400000));

    return html`
      <div class="usage-reset-info">
        Credits reset in ${daysLeft} day${daysLeft !== 1 ? "s" : ""} \u2014
        ${resetDate.toLocaleDateString(undefined, { month: "long", day: "numeric" })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-usage-view": KatanoidUsageView;
  }
}
